import { useState } from "react";
import { Box, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Article, ViewList } from "@mui/icons-material";
import { signer as signerTokens } from "theme/colors";
import editorTokens from "../SignRequestEditor.tokens";
import { InsightsTabIcon } from "./EditorIcons";
import RightPanelHeader from "./RightPanelHeader";
import { SignRequest } from "models/interfaces";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import strings from "global/constants/StringConstants";
import Summarizer from "./Summarizer";
import ContractGlance from "./ContractGlance";

type InsightsView = "summary" | "glance";

interface AIInsightsPanelProps {
  signRequest: SignRequest;
  isPublic?: boolean;
  requesToken?: string;
  hideHeader?: boolean;
  headerAction?: React.ReactNode;
  initialView?: InsightsView;
  /** Completed request: both tabs read the SIGNED copy. */
  signed?: boolean;
}

const AIInsightsPanel = ({
  signRequest,
  isPublic,
  requesToken,
  hideHeader,
  headerAction,
  initialView,
  signed,
}: AIInsightsPanelProps) => {
  const aiAssistantState = useFeatureState(FeatureKeys.AI_ASSISTANT);
  const isAIAvailable =
    aiAssistantState.status === strings.FEATURE_STATUS_AVAILABLE;

  const [view, setView] = useState<InsightsView>(initialView ?? "summary");

  // Post-sign shows the summary alone; pre-sign keeps both tabs.
  const showGlance = !signed;
  const activeView: InsightsView = showGlance ? view : "summary";

  const handleViewChange = (
    _: React.MouseEvent<HTMLElement>,
    next: InsightsView | null,
  ) => {
    if (next) setView(next);
  };

  if (!isAIAvailable) {
    return (
      <FeatureGateBlock
        variant="inline"
        status={
          aiAssistantState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
            ? strings.FEATURE_STATUS_ADMIN_DISABLED
            : strings.FEATURE_STATUS_NOT_IN_PLAN
        }
        disabledMessage={(aiAssistantState as any).disabledMessage}
      />
    );
  }

  return (
    <Stack sx={{ height: "100%" }}>
      {!hideHeader && (
        <RightPanelHeader
          icon={
            <InsightsTabIcon size={editorTokens.rightRail.headerIconSize} />
          }
          title="AI Insights"
          headerAction={headerAction}
        />
      )}

      {showGlance && (
      <Box
        sx={{
          px: 1.5,
          pt: 1.25,
          pb: 1,
        }}
      >
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={view}
          onChange={handleViewChange}
          sx={{
            width: "100%",
            alignItems: "stretch",
            backgroundColor: "signer.surface.muted",
            borderRadius: "10px",
            p: "3px",
            "& .MuiToggleButton-root": {
              flex: 1,
              minHeight: 44,
              border: "none",
              borderRadius: "8px !important",
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              lineHeight: 1.2,
              color: "signer.text.meta",
              px: 1,
              py: 0.75,
              gap: 0.5,
              whiteSpace: "normal",
              textAlign: "center",
              "&.Mui-selected": {
                backgroundColor: "signer.orange.tint",
                color: "signer.orange.dark",
                boxShadow: `0 1px 3px ${alpha(signerTokens.ink, 0.1)}`,
                "&:hover": { backgroundColor: "signer.orange.tint" },
              },
              "&:hover": { backgroundColor: "signer.surface.subtle" },
            },
          }}
        >
          <ToggleButton value="summary">
            <Article sx={{ fontSize: 16, flexShrink: 0 }} />
            <Box component="span">Summary</Box>
          </ToggleButton>
          <ToggleButton value="glance">
            <ViewList sx={{ fontSize: 16, flexShrink: 0 }} />
            <Box component="span">At a Glance</Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {activeView === "summary" ? (
          <Summarizer
            hideHeader
            signRequest={signRequest}
            isPublic={isPublic}
            requesToken={requesToken}
            signed={signed}
          />
        ) : (
          <ContractGlance
            hideHeader
            signRequest={signRequest}
            isPublic={isPublic}
            requesToken={requesToken}
          />
        )}
      </Box>
    </Stack>
  );
};

export default AIInsightsPanel;
